import React from 'react';

import debounce from 'lodash/debounce';

import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { usePublish } from '@atlassian/conversation-assistant-pubsub';

import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { updatePublish } from '../../../pm-plugins/rovo-agents/commands';

/**
 *  This component is required so we can call the usePublish hook
 *  and store the publish function in the rovo-agents plugin on mount.
 */
export const PublishToRovo = ({ editorView }: { editorView: EditorView }) => {
	const publish = usePublish('ai-mate');

	// Publish is debounced and then saved into the rovo-agents plugin state
	// to call on doc change, selection change and Editor unmount.
	React.useEffect(() => {
		const debouncedPublishDocAndSelection = debounce(({ doc, selection }) => {
			if (!doc || !selection) {
				publish({
					type: 'editor-context-payload',
					source: 'editor',
					data: undefined,
				});
				return;
			}

			// Retrieve the modal decoration set from the plugin state to access the selection state,
			// since the editor's selection is cleared when the modal opens.
			const { modalDecorationSet } = getPluginState(editorView.state);

			// Initially, assume the selected content from editorView will be used
			let selectionContent = selection.content().content;

			// Search for AI decorations within the entire document range.
			const modalDecorations = modalDecorationSet.find(0, doc.content.size);
			const existingDecoration = modalDecorations.find(
				(decoration) => decoration?.spec?.key === 'inlineDecoration',
			);

			// If an AI decoration exists, use the content within the decoration range instead
			if (existingDecoration) {
				selectionContent = doc.slice(existingDecoration.from, existingDecoration.to, true).content;
			}

			const { markdown: selectionMD } = convertProsemirrorNodeToMarkdown({
				node: Fragment.from(selectionContent),
				featureToggles: {
					markdownPlus: true,
				},
			});
			const { markdown: documentMD } = convertProsemirrorNodeToMarkdown({
				node: doc,
				featureToggles: {
					markdownPlus: true,
				},
			});

			publish({
				type: 'editor-context-payload',
				source: 'editor',
				data: {
					document: { type: 'text/markdown', content: documentMD },
					selected: { type: 'text/markdown', content: selectionMD },
				},
			});
		}, 50);

		// Initial publish on Editor init
		debouncedPublishDocAndSelection({
			doc: editorView.state.doc,
			selection: editorView.state.selection,
		});

		// Saving the debounced function to the rovo-agents plugin state
		updatePublish(debouncedPublishDocAndSelection, publish)(editorView.state, editorView.dispatch);
	}, [publish, editorView]);

	return <></>;
};
